import React, { useEffect, useState } from 'react'
import { Box, Typography, Card, Grid, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Alarm, Warning, Error } from '@mui/icons-material'
import styles from './AlertWidget.module.css'
import iconPatient from '../../assets/new-left-menu/patients.svg'
import { getFormContainer } from '../../container/form-module'
import { IAnalyticsService, ANALYTICS_SERVICE_KEY } from '../../modules/forms'
import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { User } from '../../modules/users/models/User'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { BoolQueryParam, Query } from '../../common/api/Query'
import { PageLayout } from 'pages/layout'

// Obtener servicios
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)
const formContainer = getFormContainer()
const analyticsService = formContainer.get<IAnalyticsService>(ANALYTICS_SERVICE_KEY)

interface AlarmHistory {
  id: string
  userId: string
  alarmType: string
  triggeredAt: string
  resolvedAt: string | null
  isActive: boolean
}

interface PatientAlarm {
  patient: User
  medication: boolean
  food: boolean
  sleep: boolean
  alarmCount: number
  alarmHistory: AlarmHistory[]
}

export const AlertWidget = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [patients, setPatients] = useState<User[]>([])
  const [patientAlarms, setPatientAlarms] = useState<PatientAlarm[]>([])
  const [loggedUserData, setLoggedUserData] = useState<User | null>(null)

  useEffect(() => {
    const loggedUser = loggedUserService.get()
    if (loggedUser?.id) {
      userService.getByID(loggedUser.id).subscribe((userData) => {
        if (userData) {
          setLoggedUserData(userData)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (!loggedUserData) return

    const queryParams = [
      {
        name: 'isPatient',
        value: new BoolQueryParam(true),
      },
    ]

    setIsLoading(true)

    userService
      .getFilteredList(
        new Query({
          query: queryParams,
          pager: { offset: 0, limit: 100 },
        })
      )
      .subscribe((res) => {
        setPatients(res.items)
        loadPatientAlarms(res.items)
      })
  }, [loggedUserData])

  const loadPatientAlarms = async (patientsList: User[]) => {
    const alarms: PatientAlarm[] = []

    for (const patient of patientsList) {
      try {
        const [alarmData, alarmHistory] = await Promise.all([
          analyticsService.getAlarmStatus(patient.id).toPromise(),
          analyticsService.getAlarmHistory?.(patient.id).toPromise() || Promise.resolve([]),
        ])

        if (alarmData) {
          const alarmCount = [alarmData.medication, alarmData.food, alarmData.sleep].filter(
            Boolean
          ).length

          alarms.push({
            patient: patient,
            medication: alarmData.medication,
            food: alarmData.food,
            sleep: alarmData.sleep,
            alarmCount,
            alarmHistory: (alarmHistory as AlarmHistory[]) || [],
          })
        } else {
          alarms.push({
            patient: patient,
            medication: false,
            food: false,
            sleep: false,
            alarmCount: 0,
            alarmHistory: [],
          })
        }
      } catch (error) {
        alarms.push({
          patient: patient,
          medication: false,
          food: false,
          sleep: false,
          alarmCount: 0,
          alarmHistory: [],
        })
      }
    }

    setPatientAlarms(alarms)
    setIsLoading(false)
  }

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  }

  const translateAlarmType = (type: string): string => {
    const translations: { [key: string]: string } = {
      medication: t('medication'),
      food: t('food'),
      sleep: t('sleep'),
    }
    return translations[type] || type
  }

  const patientsWithAlarms = patientAlarms.filter((p) => p.alarmCount > 0)
  const criticalPatients = patientAlarms.filter((p) => p.alarmCount >= 2)
  const stablePatients = patientAlarms.filter((p) => p.alarmCount === 0)
  const activeAlarmPatients = patientAlarms.filter((p) => p.alarmCount === 1)

  const allActiveAlarms = patientsWithAlarms.flatMap((patientAlarm) => {
    const patient = patientAlarm.patient
    const patientName = `${patient.firstName} ${patient.lastName}`

    return patientAlarm.alarmHistory
      .filter((alarm) => alarm.isActive)
      .map((alarm) => ({
        patientName,
        alarmType: translateAlarmType(alarm.alarmType),
        triggeredAt: alarm.triggeredAt,
      }))
  })

  const sortedAlarms = allActiveAlarms.sort(
    (a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime()
  )

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress size={60} className={styles.progressIndicator} />
      </Box>
    )
  }

  return (
    <PageLayout title={t('hello')} withCard={false}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${styles.statsCard} ${styles.usersCard}`}>
            {/* Icono primero y titulo despues, como las otras tres tarjetas:
                esta era la unica al reves. El tamaño tambien se iguala (los
                iconos de las demas son de 24px). */}
            <Box className={styles.statsIconContainer}>
              <Box
                component="img"
                src={iconPatient}
                alt="patients icon"
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  objectFit: 'contain',
                  filter:
                    'brightness(0) saturate(100%) invert(73%) sepia(13%) saturate(2060%) hue-rotate(26deg) brightness(96%) contrast(80%)',
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center', mr: '32px' }}
              >
                {t('users')}
              </Typography>
            </Box>
            <Typography variant="h2" className={styles.statsValue}>
              {patients.length}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${styles.statsCard} ${styles.stableCard}`}>
            <Box className={styles.statsIconContainer}>
              <Warning sx={{ fontSize: 20, color: 'var(--success)' }} />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center', mr: '32px' }}
              >
                {t('stableUsers')}
              </Typography>
            </Box>
            <Typography variant="h2" className={styles.statsValue}>
              {stablePatients.length}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${styles.statsCard} ${styles.criticalCard}`}>
            <Box className={styles.statsIconContainer}>
              <Error sx={{ fontSize: 20, color: 'var(--error)' }} />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center', mr: '32px' }}
              >
                {t('criticalAlarms')}
              </Typography>
            </Box>
            <Typography variant="h2" className={styles.statsValue}>
              {criticalPatients.length}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`${styles.statsCard} ${styles.moderateCard}`}>
            <Box className={styles.statsIconContainer}>
              <Alarm sx={{ fontSize: 20, color: 'var(--warning)' }} />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center', mr: '32px' }}
              >
                {t('moderateAlarms')}
              </Typography>
            </Box>
            <Typography variant="h2" className={styles.statsValue}>
              {activeAlarmPatients.length}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Card className={styles.alertsCard}>
        <Typography variant="h6" className={styles.alertsTitle}>
          {t('usersWithAlerts')}
        </Typography>

        <Box className={styles.tableContainer}>
          <table className={styles.alertsTable}>
            <thead>
              <tr className={styles.tableHeader}>
                <th>{t('time')}</th>
                <th>{t('user')}</th>
                <th>{t('alert')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedAlarms.map((alarm, index) => (
                <tr
                  key={`${alarm.patientName}-${alarm.alarmType}-${index}`}
                  className={styles.tableRow}
                >
                  <td className={styles.tableCell}>{formatTime(alarm.triggeredAt)}</td>
                  <td className={styles.tableCell}>{alarm.patientName}</td>
                  <td className={styles.tableCell}>{alarm.alarmType}</td>
                </tr>
              ))}

              {sortedAlarms.length === 0 && (
                <tr>
                  <td colSpan={3} className={styles.emptyState}>
                    {t('noAlertsFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Card>
    </PageLayout>
  )
}
